/**
 * https://developers.google.com/maps/documentation/javascript/tutorial#api_key
 * https://developers.google.com/maps/faq#usagelimits 
 * (consider enrolling for automated billing, when we go over or purchasing a business license from https://code.google.com/apis/console/#project:445422974445)
 */
define('components/map',['require','services/bookingService','services/userService','utils'],function (require) {
    'use strict';
    var bookingService = require('services/bookingService');
    var userService = require('services/userService');
    var utils = require('utils');

    // Hotel property image icons
    var HOTEL_ICON = 'https://adestinn2blob.blob.core.windows.net/images/map/hotel_icon.svg';
    var BORDER_HOTEL_ICON = 'https://adestinn2blob.blob.core.windows.net/images/map/border_hotel_icon.svg';

    // The coordinates of the center of the United States, default when no destination was entered or when map
    // is first initialized without any previous saved region searches
    var UNITED_STATES_CENTER = { lat: 39.443256, lng: -98.95734 };

    // Map styling options
    var MAP_STYLES = [
        { 'featureType': 'poi.business.lodging', 'stylers': [{ 'visibility': 'off' }] }, // Remove lodging from google maps view
        { 'elementType': 'geometry.fill', 'stylers': [{ 'color': '#fefdfa' }] }, // Land #fefdfa
        { 'featureType': 'water', 'stylers': [{ 'color': '#a2c9f6' }, { 'lightness': 18 }] }, // Water #bfdaf9
        { 'featureType': 'road.highway', 'elementType': 'geometry', 'stylers': [{ 'visibility': 'on' }, { 'weight': 0.4 }, { 'color': '#ffd5b5' }] }, // Roads #ffd5b5
        { 'featureType': 'administrative.country', 'elementType': 'labels' }, 
        { 'featureType': 'administrative.country', 'elementType': 'labels.text', 'stylers': [{ 'visibility': 'off' }] }, 
        { 'featureType': 'administrative.province', 'elementType': 'labels', 'stylers': [{ 'visibility': 'off' }] }, 
        { 'featureType': 'administrative.country', 'elementType': 'geometry', 'stylers': [{ 'visibility': 'on' }, { 'color': '#f3f3f3' }] }, // Country Border #f3f3f3
        { 'featureType': 'road.local', 'stylers': [{ 'color': '#808080' }, { 'visibility': 'simplified' }, { 'lightness': 86 }] }, 
        { 'featureType': 'road.arterial', 'stylers': [{ 'visibility': 'on' }, { 'lightness': 37 }] }, 
        { 'featureType': 'administrative.province', 'elementType': 'geometry', 'stylers': [{ 'visibility': 'on' }, { 'lightness': 73 }] }
    ];

    /**
     * Map Constructor
     * @param {html element} $el map container element
     * @param {Array<DisplayRegion>} regions id, value, keywords, Count, Latitude, Longitude
     * @param {Date} startDate from date for search selection
     * @param {Date} endDate to date for search selection
     * @param {func} onLoad 
     */
    var MapController = function ($el, regions, startDate, endDate) {
        if ($el == null) {
            throw new Error('$el is null!');
        }
        this.loaded = false;
        this.onLoadHandlers = [];

        this.regionMarkers = [];
        this.hotelMarkers = [];
        this.noRegionHotelMarkers = []; // holds hotel properties that do not have an assigned region
        this.hotelCluster = null;

    this.dateChanged = false;
    this.daysOfStayLength = 1; //how long of a stay we are searching for
        this.startDate = startDate;
        this.endDate = endDate;
        this.regions = regions;

    this.hotelsWithoutRegionId = null; //stores result for getmapdestinations when regionId=0
    this.hotelsWithoutRegionIdRequest = null;
    this.lastUsedRegionId = 0;

    this.mapLoadedRegionsToHotels = new Map(); //maps regions to loaded hotels 

        this.mapSearchItems = utils.getLastViewedRegion();

        this.mapZoom = this.mapSearchItems.zoom;
        this.minHotelZoom = 8;
        this.mapCenter = { lat: this.mapSearchItems.center.lat, lng: this.mapSearchItems.center.lng };

        this.plotLocationHandler = this.plotLocation.bind(this);
        var plotDestinations = function() {
            this.init($el[0]);
            this.loaded = true;
            this.onLoadHandlers.forEach(function(handler) { handler(); });
        }.bind(this);
        if (utils.tryGetValue(window, 'google.maps') == null) {
            require(['https://maps.google.com/maps/api/js?key=AIzaSyCsSZVOaoUrsxLLYvmT-_nnoot198jg2hA&libraries=geometry', 'https://unpkg.com/@googlemaps/markerclusterer/dist/index.min.js'], plotDestinations);
        } else {
            plotDestinations();
        }
    };
    var proto = MapController.prototype;

    /**
     * Initialization of the google map on the page
     * 
     * Note: we put map on the window, since there is no official way to destroy a map, 
     * so we use the same instance everytime
     * @param {html element} $el map container element
     */
    proto.init = function ($el) {
        window.map = new window.google.maps.Map($el, this.getMapOptions());

        window.google.maps.event.addListenerOnce(window.map, 'tilesloaded', function () {
            var resetMapDiv = document.createElement('div');
            var resetMapButton = this.resetMap();
            resetMapButton.setAttribute('id', 'map-reset');
            resetMapDiv.appendChild(resetMapButton);
            window.map.controls[google.maps.ControlPosition.TOP_LEFT].push(resetMapDiv);

            if (userService.inRole('Admin') || userService.inRole('CallCenter') || userService.inRole('HelpDesk')) {
                this.displayLodging();
            }

            this.viewport = this.getView();

            if (this.regions.length <= 0) {
                return;
            }

            // Create the hotel clusterer before hotels are plotted so markers can be added safely during update().
            this.hotelCluster = new window.markerClusterer.MarkerClusterer({
                map: window.map,
                markers: [],
                renderer: this.getClustererOptions(),
            });

        this.loadHotelsWithoutRegion();
            this.loadMap(this.regions);

            var searched = utils.getLastSearch();
            var lastMapSearch = utils.getLastViewedRegion();

            // If a user enters a destination to search for and then toggles the map view 
            // from OFF to ON
            if (searched.region != null && lastMapSearch.region == null) {
                this.updateMapView();
            }
            
            this.update(this.regions);


        }.bind(this));
    };

    /**
     * This function is only called during the map initialization ** after ** the map has loaded.
     * An event listener is then attached to the map to find the view and update the markers
     * when the map idles after the view has changed 
     */
    proto.loadMap = function () {
        window.google.maps.event.addListener(window.map, 'idle', function () {
            this.viewport = this.getView();
            this.update(this.regions); // when the map viewport changes, we must re-update the map for the 
        }.bind(this));
    };

    /**
     * Finds the current view of the google map and will create a polygon object that will be used to
     * determine which markers are contained
     * 
     * @returns {google.maps.Polygon} polygon object that allows the view check if markers are 
     * contained within it
     */
    proto.getView = function () {
        // Grab the coordinates of each corner of the map viewport
        var ne = new window.google.maps.Marker({ position: window.map.getBounds().getNorthEast()});
        var nw = new window.google.maps.Marker({ position: new window.google.maps.LatLng(window.map.getBounds().getNorthEast().lat(), window.map.getBounds().getSouthWest().lng())});
        var se = new window.google.maps.Marker({ position: new window.google.maps.LatLng(window.map.getBounds().getSouthWest().lat(), window.map.getBounds().getNorthEast().lng())});
        var sw = new window.google.maps.Marker({ position: window.map.getBounds().getSouthWest()});

        var view = [
            nw.getPosition(),
            ne.getPosition(),
            se.getPosition(),
            sw.getPosition(),
        ];

        this.mapZoom = window.map.getZoom();

        return new window.google.maps.Polygon({
            paths: view,
            strokeOpacity: 0.0,
            strokeWeight: 2,
            fillOpacity: 0.0,
            map: window.map,
        });
    };

    /**
     * Reads in a marker and will check to see if the marker is currently viewed on the map
     * 
     * @param {google.maps.Marker} marker the current marker in the view
     */
    proto.withinView = function(marker) {
        return google.maps.geometry.poly.containsLocation(marker.getPosition(), this.viewport);
    };

    /**
     * Reads in a marker and will check to see if the marker is within specified distance of the map view
     * bounds
     * 
     * The 'tolerance' is measured by coordinate distance away from edge of the map viewport window,
     * 1.0 is approximately 69 miles, 1.44927536 is 100 miles from the edge and this is chosen due to
     * the maximum option on the search form for expanding the search results
     * 
     * @param {google.maps.Marker} marker the marker near the view
     */
    proto.nearView = function(marker) {
        return google.maps.geometry.poly.isLocationOnEdge(marker.getPosition(), this.viewport);
    };

    proto.updateMapView = function() {
        var searched = utils.getLastSearch();
        
        // If the search form destination is empty, clear, or no results were found, map view returns to default
        if (searched.region == null || searched.region.value === '' || searched.region.value === 'No results found') {            
            this.mapCenter = UNITED_STATES_CENTER;
            // If the map is zoomed out less than the initial default zoom, mapZoom should be left the same
            this.mapZoom = window.map.getZoom() < 5 ? window.map.getZoom() : 5; 
            
            var defaultView = {
                region: null,
                zoom: this.mapZoom,
                center: this.mapCenter,
            };

            window.map.panTo(new window.google.maps.LatLng(defaultView.center.lat, defaultView.center.lng));
            window.map.setZoom(defaultView.zoom);

            this.viewport = this.getView();

            utils.setLastViewedRegion(defaultView);
        } else {
            var foundRegion = false;
            var savedRegion = null;

            this.regions.forEach(function(region) {
                if (region.value === searched.region.value) {
                    savedRegion = searched.region;
                    foundRegion = true;

                    this.mapCenter = { lat: region.Latitude, lng: region.Longitude };
                    this.mapZoom = this.minHotelZoom;

                    var searchedRegion = {
                        region: searched.region,
                        zoom: this.mapZoom,
                        center: this.mapCenter,
                    };

                    window.map.panTo(new window.google.maps.LatLng(searchedRegion.center.lat, searchedRegion.center.lng));
                    window.map.setZoom(this.minHotelZoom);

                    this.viewport = this.getView();
    
                    utils.setLastViewedRegion(searchedRegion);
                }
            }.bind(this));
            if (!foundRegion) {
                console.log('\nNo region found.\n\n');
            } else {
                this.recordMapSearch(savedRegion, 'Search box');
            }
        }
    };

    proto.recordMapSearch = function (region, searchType) {
        var searchItems = utils.getLastSearch();

        // Region or Hotel marker was clicked, save map search stats
        $.postJSON('/api/hotels/mapsearch', {
            RegionId: region == null ? 0 : region.id,
            SearchType: searchType, // Map property, Map, Search box
            FromDate: this.startDate,
            ToDate: this.endDate,
            Adults: searchItems.adults,
            Children: searchItems.children,
            Coordinates: { 
                Latitude: this.mapCenter.lat, 
                Longitude: this.mapCenter.lng,
            },
        }).then(function () {
            return;
        }.bind(this));
    };

    /**
     * Clears the map of exisiting markers and re-plots the new region markers and corresponding hotel markers.
     * 
     * @param {Array<DisplayRegion>} regions id, value, keywords, Count, Latitude, Longitude
     */
    proto.update = function (regions) {
        this.clearMarkers();

        var chain = Promise.resolve();
        regions.forEach(function (region) {
            chain = chain.then(function() { return this.plotLocation(region); }.bind(this));
        }.bind(this));

        chain.then(function() {
            if (this.hotelCluster !== null) {
                this.hotelCluster.render(); //Render once after all regions finish so clusters are recalculated only one time.
            }
        }.bind(this));
    };

    proto.hasNoRegion = function(region) {
        return region.id === 0;
    };

    proto.plotLocation = function (region) {
        return new Promise(function(resolve) {
            var maps = window.google.maps;
    
            // there is no region to pin on the map, plot hotel at region level
            if (this.hasNoRegion(region)) {
                this.plotHotels(region).then(resolve);
            } else {
                var position = new maps.LatLng(region.Latitude, region.Longitude);
            
                var marker = new maps.Marker({
                    map: window.map,
                    position: position,
                    title: region.value,
                });
                // If hotels were already loaded for this region, still allow them to be replotted. Fixes non-plotting issue when clicking into a cluster
                var regionInView = this.withinView(marker) || this.nearView(marker);
                var hasLoadedHotels = this.mapLoadedRegionsToHotels.has(region.id);

                if (regionInView) {
                    this.regionMarkers.push(marker);
                    marker.setMap(window.map);
                } else {
                    marker.setMap(null);
                }

                //already loaded hotel data should also redraw here after cluster zooms.
                if (window.map.getZoom() >= this.minHotelZoom && (regionInView || hasLoadedHotels)) {
                    this.plotHotels(region).then(function() {
                        marker.setMap(null); // Hotels are being displayed, remove region pin from map
                        resolve();
                    });
                    return;
                }

                if (!region.noClick) {
                    maps.event.addListener(marker, 'click', this.onMarkerClick(region));
                }
                resolve();
            }
        }.bind(this));
    };

    proto.plotHotels = function (region) {
    var hotelsPromise = null;
    var loadedHotelsForRegion = this.mapLoadedRegionsToHotels.get(region.id);

    if (this.hasNoRegion(region)) {
      hotelsPromise = this.loadHotelsWithoutRegion(); //regionId = 0
    } else if (loadedHotelsForRegion) {
      //region data is already loaded in this window

      hotelsPromise = Promise.resolve(loadedHotelsForRegion);
    } else {

      hotelsPromise = $.get(
        "/api/hotels/getmapdestinations/?regionId={0}&fromDate={1}&toDate={2}".format(
          region.id,
          this.startDate,
          this.endDate
        )
      ).then(
        function (hotels) {
          //set mapping from region.id to its hotels
          this.mapLoadedRegionsToHotels.set(region.id, hotels);
          return hotels;
        }.bind(this)
      );
    }
    return hotelsPromise.then(
      function (hotels) {
        if (this.hotelMarkers === null || this.noRegionHotelMarkers === null) {
          return;
        }

        hotels.Properties.forEach(
          function (hotel) {
            if (
              this.hotelMarkers !== null &&
              this.noRegionHotelMarkers !== null
            ) {
              hotel.href = bookingService.getDetailLink(
                hotel.HotelCode,
                hotel.ProviderCode,
                hotel.Region
              );
    
              var position = new window.google.maps.LatLng(
                hotel.Latitude,
                hotel.Longitude
              );

              var iconType =
                window.map.getMapTypeId() === "satellite" ||
                window.map.getMapTypeId() === "hybrid";
    
                    var marker = new window.google.maps.Marker({
                        map: null, // don't place the marker immediately, we add it below only if it is visible or should be clustered
                        position: position,
                        icon: {
                            // Font Awesome Pro 6.2.0 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2022 Fonticons, Inc.
                            url: iconType ? BORDER_HOTEL_ICON : HOTEL_ICON,
                  scaledSize: iconType ? new google.maps.Size(40, 35) : new google.maps.Size(35, 35),
                        },
                        title: hotel.HotelName
                    });
    
              if (this.hasNoRegion(region)) {
                if (this.withinView(marker)) {
                  marker.setMap(window.map);
                }
                this.noRegionHotelMarkers.push(marker);
              } else {
                this.hotelMarkers.push(marker);
              }
    
                    if (!hotel.noClick) {
                window.google.maps.event.addListener(
                  marker,
                  "click",
                  this.onMarkerClick(hotel)
                );
                    }
                }
          }.bind(this)
        );

            if (this.hotelMarkers !== null && !this.hasNoRegion(region)) {
                var tempHotelMarkers = [];
                var uniqueHotelMarkers = []; // stores only one marker per position so duplicates are not clustered

                if (this.hotelCluster !== null) {
                    this.hotelCluster.clearMarkers();
                }

                // Fix duplicate hotels that appear when moving map/zooming too quickly
                this.hotelMarkers.forEach(function(marker) {
                    // Checks if the array already includes the marker, if it is already added... remove it
                    if (!tempHotelMarkers.includes(marker.getPosition().toString())) {
                        tempHotelMarkers.push(marker.getPosition().toString());
                        uniqueHotelMarkers.push(marker);

                        if ((this.withinView(marker)) && window.map.getZoom() >= this.minHotelZoom && this.hotelCluster !== null) {
                            this.hotelCluster.addMarker(marker, true); //pass in true to allow markers to redraw all at once using render()
                        }
                    } else {
                        marker.setMap(null);
                    }
                }.bind(this));

                this.hotelMarkers = uniqueHotelMarkers;

                if (this.hotelCluster !== null) {
                    this.hotelCluster.render();
                }
            }
        }.bind(this));
    };


  proto.loadHotelsWithoutRegion = function () {
    // Reuse local result if dates did not change
    if (this.hotelsWithoutRegionId !== null && !this.dateChanged) {
      return Promise.resolve(this.hotelsWithoutRegionId);
    }

    // Reuse active request if one is already running
    if (this.hotelsWithoutRegionIdRequest !== null) {
      return this.hotelsWithoutRegionIdRequest;
    }

    this.hotelsWithoutRegionIdRequest = $.get(
      "/api/hotels/getmapdestinations/?regionId=0&fromDate={0}&toDate={1}".format(
        this.startDate,
        this.endDate
      )
    ).then(
      function (hotels) {
        this.hotelsWithoutRegionId = hotels; //store results
        this.hotelsWithoutRegionIdRequest = null; //clear active request check
        this.dateChanged = false; //reset datechanged check
        return hotels;
      }.bind(this)
    );

    return this.hotelsWithoutRegionIdRequest;
  };

  proto.plotByLatLng = function (hotelName, lat, lng) {
        var position = new window.google.maps.LatLng(lat, lng);

        var iconType = window.map.getMapTypeId() === 'satellite' || window.map.getMapTypeId() === 'hybrid';

        var marker = new window.google.maps.Marker({
            position: position,
            icon: {
                // Font Awesome Pro 6.2.0 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2022 Fonticons, Inc.
                url: iconType ? BORDER_HOTEL_ICON : HOTEL_ICON,
                scaledSize: iconType ? new google.maps.Size(40, 35) : new google.maps.Size(35, 35),
            },
            title: hotelName,
        });

        window.map.panTo(position);
        window.map.setZoom(this.minHotelZoom);

        // Override and pan over to hotel marker on pop-up map in modal to view hotel location and 
        // surrounding areas without changing mapCenter if user navigates back to search page
        window.google.maps.event.addListenerOnce(window.map, 'idle', function () {
            window.google.maps.event.addListenerOnce(window.map, 'tilesloaded', function () {
                window.map.panTo(position);
                window.map.setZoom(this.minHotelZoom);
            }.bind(this));
        }.bind(this));

        marker.setMap(window.map);
    };

    /**
     * Helper function to determine if a marker is a hotel or a region pin
     * 
     * @param {*} destination hotel or region
     * @returns true: hotel, false: region
     */
    proto.isHotel = function(destination) {
        return destination.HotelCode != null;
    };

    proto.onMarkerClick = function(destination) {
        return function(event) {
            var type = this.isHotel(destination);

            window.gaTrack('destinations', 'map', type + '_click_' + destination.n + (destination.i == null ? '' : ': ' + destination.i, 1));

            if (type) { // Hotel
                window.open(destination.href, '_self');
                this.destroy();

            } else { // Region
                window.map.panTo(new window.google.maps.LatLng(destination.Latitude, destination.Longitude));
                window.map.setZoom(this.minHotelZoom);
            }

            this.mapCenter = type ? 
                (this.hasNoRegion(destination.Region) ? 
                    { lat: destination.Latitude, lng: destination.Longitude } : 
                    { lat: destination.Region.Latitude, lng: destination.Region.Longitude }) : 
                { lat: destination.Latitude, lng: destination.Longitude };
            
            var savedRegion = type ? 
                (this.hasNoRegion(destination.Region) ? 
                    null : destination.Region) : 
                destination;

            var lastViewed = {
                region: savedRegion,
                zoom: this.minHotelZoom,
                center: this.mapCenter,
            };

            // If a no region hotel is clicked, the search bar will be cleared when navigating back to the page
            if (type && this.hasNoRegion(destination.Region)) {
                var search = utils.getLastSearch();
                search.region = null;
                utils.setLastSearch(search);
            }

            utils.setLastViewedRegion(lastViewed);
            this.recordMapSearch(savedRegion, type ? 'Map property' : 'Map');
        }.bind(this);
    };

    /**
     * Update the values returned from the 
     * @param {Date} startDate 
     * @param {Date} endDate 
     * @param {Array<DisplayRegion>} regions id, value, keywords, Count, Latitude, Longitude
     */
  proto.updateMapSearchData = function (startDate, endDate, regions) {
    var daysBetweenDates = this.getDaysBetween(startDate, endDate);
    if (this.daysOfStayLength !== daysBetweenDates) { //check if the length of the stay has changed (1 day, 2 day)
      this.daysOfStayLength = daysBetweenDates;
      this.dateChanged = true;
      this.hotelsWithoutRegionId = null;
      this.hotelsWithoutRegionIdRequest = null;
      this.mapLoadedRegionsToHotels.clear();
      this.loadHotelsWithoutRegion();
    }

        this.startDate = startDate;
        this.endDate = endDate;
        this.regions = regions;
    };

    /**
     * Execute the given function upon the map being fully loaded
     * @param {func} handler 
     */
    proto.onLoad = function(handler) {
        this.loaded ? handler() : this.onLoadHandlers.push(handler);
    };

    /**
     * Default map options
     */
    proto.getMapOptions = function () {
        return {
            minZoom: 4,
            zoom: this.mapZoom, 
            center: new window.google.maps.LatLng(this.mapCenter.lat, this.mapCenter.lng), 
            mapTypeId: window.google.maps.MapTypeId.ROADMAP,
            styles: MAP_STYLES,
            // Restriction of map bounds so user can not scroll into a grey area 'off' the map
            restriction: {
                latLngBounds: {
                    north: 85,
                    south: -85,
                    west: -180,
                    east: 180
                }
            },
        };
    };

    proto.resetMap = function () {
        var resetButton = document.createElement('button');

        resetButton.style.backgroundColor = '#fff';
        resetButton.style.border = '0px solid #fff';
        resetButton.style.borderRadius = '2px';
        resetButton.style.boxShadow = '0px 1px 4px -1px rgba(0,0,0,.3)';
        resetButton.style.color = 'rgb(25,25,25)';
        resetButton.style.cursor = 'pointer';
        resetButton.style.fontFamily = 'Roboto,Arial,sans-serif';
        resetButton.style.fontSize = '18px';
        resetButton.style.lineHeight = '40px';
        resetButton.style.margin = '10px 0';
        resetButton.style.padding = '0 17px';
        resetButton.style.textAlign = 'center';

        resetButton.textContent = 'Re-Center';
        resetButton.title = 'Click to re-center the map';
        resetButton.type = 'button';

        resetButton.addEventListener('click', function() {
            this.mapZoom = 5;
            this.mapCenter = UNITED_STATES_CENTER;

            window.map.panTo(new window.google.maps.LatLng(this.mapCenter.lat, this.mapCenter.lng));
            window.map.setZoom(this.mapZoom);

            utils.setLastViewedRegion({
                region: null,
                zoom: this.mapZoom,
                center: this.mapCenter,
            });
            utils.setLastViewedDestination(null);
        }.bind(this));
        
        return resetButton;
    };

    proto.displayLodging = function () {
        var toggleLodgingDiv = document.createElement('div');
        toggleLodgingDiv.style.display = 'flex';
        toggleLodgingDiv.style.alignItems = 'center';

        var toggleLabel = document.createElement('label');
        toggleLabel.textContent = 'Display Lodging: ';
        toggleLabel.htmlFor = 'lodge-toggle';
        toggleLabel.style.margin = '0px';
        toggleLabel.style.color = 'rgb(25,25,25)';
        toggleLabel.style.fontFamily = 'Roboto,Arial,sans-serif';
        toggleLabel.style.fontSize = '18px';
        toggleLodgingDiv.appendChild(toggleLabel);

        var toggleSwitch = document.createElement('label');
        toggleSwitch.setAttribute('class', 'toggle-switch');
        toggleLodgingDiv.appendChild(toggleSwitch);

        var lodgeToggle = document.createElement('input');
        lodgeToggle.setAttribute('id', 'lodge-toggle');
        lodgeToggle.type = 'checkbox';
        toggleSwitch.appendChild(lodgeToggle);

        lodgeToggle.addEventListener('change', function() {
            if (lodgeToggle.checked) {
                MAP_STYLES[0] = { 'featureType': 'poi.business.lodging', 'stylers': [{ 'visibility': 'on' }] };
                window.map.setOptions({ styles: MAP_STYLES });
            } else {
                MAP_STYLES[0] = { 'featureType': 'poi.business.lodging', 'stylers': [{ 'visibility': 'off' }] };
                window.map.setOptions({ styles: MAP_STYLES });
            }
        });

        var slider = document.createElement('span');
        slider.setAttribute('class', 'slider');
        toggleSwitch.appendChild(slider);

        toggleLodgingDiv.style.backgroundColor = '#fff';
        toggleLodgingDiv.style.border = '0px solid #fff';
        toggleLodgingDiv.style.borderRadius = '2px';
        toggleLodgingDiv.style.boxShadow = '0px 1px 4px -1px rgba(0,0,0,.3)';
        toggleLodgingDiv.style.cursor = 'pointer';
        toggleLodgingDiv.style.height = '40px';
        toggleLodgingDiv.style.margin = '10px';
        toggleLodgingDiv.style.padding = '0 17px';
        toggleLodgingDiv.title = 'Show lodging on map';
        window.map.controls[google.maps.ControlPosition.TOP_LEFT].push(toggleLodgingDiv);
    };

    proto.getClustererOptions = function () {
        var renderer = {
            render({ count, position }) {
                var color = count < 3 ? '#009900' : count < 6 ? '#ff9900' : count < 9 ? '#ff6600' : '#ff0000';
                var size = count < 3 ? 45 : count < 6 ? 50 : count < 9 ? 55 : 60;

                var svg = window.btoa('<svg fill="' + color +'" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240"><circle cx="120" cy="120" opacity=".6" r="70" /><circle cx="120" cy="120" opacity=".3" r="90" /><circle cx="120" cy="120" opacity=".2" r="110" /></svg>');
                
                return new google.maps.Marker({
                    position: position,
                    icon: {
                        url: 'data:image/svg+xml;base64,' + svg,
                        scaledSize: new google.maps.Size(size, size),
                    },
                    label: {
                        text: count.toString(),
                        color: 'rgba(255,255,255,0.9)',
                        fontSize: '12px',
                    },
                    title: count.toString() + ' Hotels',
                });
            }
        };
        return renderer;
    };

    /**
     * Remove markers from map and then clear them from the map controller
     */
    proto.clearMarkers = function () {
        if (this.regionMarkers !== null) {
            this.regionMarkers.forEach(function(m) {
                m.setMap(null);
            }.bind(this));
        }
        
        if (this.noRegionHotelMarkers !== null) {
            this.noRegionHotelMarkers.forEach(function(m) {
                m.setMap(null);
            }.bind(this));
        }

        if (this.hotelMarkers !== null) {
            if (this.hotelCluster !== null) {
                this.hotelCluster.clearMarkers();
            }

            this.hotelMarkers.forEach(function(m) {
                m.setMap(null);
            }.bind(this));
        }

        this.regionMarkers = [];
        this.noRegionHotelMarkers = [];
        this.hotelMarkers = [];
    };

    proto.destroy = function () {
        if (window.map != null) { 
            window.map.unbindAll(); 
        }
        this.clearMarkers();
        this.regionMarkers = null;
        this.noRegionHotelMarkers = null;
        this.hotelMarkers = null;
        this.hotelCluster.setMap(null);
        this.onLoad = null;
        this.plotLocationHandler = null;
        return;
    };

    proto.getDaysBetween = function(startDate, endDate) {
        var start = new Date(startDate);
        var end = new Date(endDate);

        var diffMs = end - start;
        var diffInDays = diffMs / (1000 * 60 * 60 * 24);

        if (diffInDays >= 2) { //max stay length we query for
            return 2;
        }
        return diffInDays; //convert ms to days
    };
    return MapController;
});

